// SPDX-License-Identifier: AGPL-3.0-or-later
import type {Message} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {action, makeAutoObservable} from 'mobx';

type TypingUser = Readonly<{
	timeout: NodeJS.Timeout;
	expiresAt: number;
}>;
const TYPING_TIMEOUT = 10000;
const EMPTY_TYPING_USERS: ReadonlyArray<string> = Object.freeze([]);
class TypingIndicator {
	typingUsersByChannel: Record<string, Record<string, TypingUser>> = {};
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	getTypingUsers(channelId: string): ReadonlyArray<string> {
		const entries = this.typingUsersByChannel[channelId];
		if (!entries) return EMPTY_TYPING_USERS;
		return Object.keys(entries);
	}
	isTyping(channelId: string, userId: string): boolean {
		return this.typingUsersByChannel[channelId]?.[userId] !== undefined;
	}
	getCount(channelId: string): number {
		const entries = this.typingUsersByChannel[channelId];
		if (!entries) return 0;
		let count = 0;
		for (const _ in entries) count++;
		return count;
	}
	@action
	reset(): void {
		this.clearAllTimeouts();
		this.typingUsersByChannel = {};
	}
	@action
	startTyping(channelId: string, userId: string): void {
		const existingTimeout = this.typingUsersByChannel[channelId]?.[userId]?.timeout;
		if (existingTimeout) {
			clearTimeout(existingTimeout);
		}
		const newTimeout = this.scheduleClear(channelId, userId);
		if (!this.typingUsersByChannel[channelId]) {
			this.typingUsersByChannel[channelId] = {};
		}
		this.typingUsersByChannel[channelId][userId] = {
			timeout: newTimeout,
			expiresAt: Date.now() + TYPING_TIMEOUT,
		};
	}
	@action
	stopTyping(channelId: string, userId: string): void {
		const channelUsers = this.typingUsersByChannel[channelId];
		if (!channelUsers?.[userId]) {
			return;
		}
		clearTimeout(channelUsers[userId].timeout);
		delete this.typingUsersByChannel[channelId][userId];
		if (Object.keys(this.typingUsersByChannel[channelId]).length === 0) {
			delete this.typingUsersByChannel[channelId];
		}
	}
	stopTypingOnMessageCreate(message: Message): void {
		this.stopTyping(message.channel_id, message.author.id);
	}
	private scheduleClear(channelId: string, userId: string): NodeJS.Timeout {
		return setTimeout(() => this.stopTyping(channelId, userId), TYPING_TIMEOUT);
	}
	private clearAllTimeouts(): void {
		for (const channelUsers of Object.values(this.typingUsersByChannel)) {
			for (const user of Object.values(channelUsers)) {
				clearTimeout(user.timeout);
			}
		}
	}
}
export default new TypingIndicator();
